/**
 * LG Wishlist | Prototype du raccordement compte client.
 * Cette version N'ACTIVE PAS la synchronisation automatique.
 * Aucun jeton n'est écrit dans localStorage ou sessionStorage.
 * Opérations explicites pour tester en toute sécurité :
 *   LGWishlistSync.connect()
 *   LGWishlistSync.status()
 *   LGWishlistSync.readRemote()
 *   LGWishlistSync.mergeLocal()
 *   LGWishlistSync.disconnect()
 *
 * Les favoris anonymes restent dans la clé existante.
 * Les favoris privés du compte ne sont jamais copiés en local.
 */
(() => {
  'use strict';

  if (window.LGWishlistSync) return;

  const SHOP_ORIGIN = 'https://legarageknitwear.com';
  const WORKER_ORIGIN = 'https://legarage-favoris.sparkling-disk-fb8a.workers.dev';
  let sessionToken = null;
  let accountItems = null;
  let connecting = null;

  function checkStorefrontOrigin() {
    if (location.origin !== SHOP_ORIGIN) {
      throw new Error(
        'LG Wishlist : ouvrir la prévisualisation sur ' + SHOP_ORIGIN +
        ' pour tester la connexion Shopify.'
      );
    }
  }

  function status() {
    return {
      connected: Boolean(sessionToken),
      remoteCount: accountItems ? accountItems.length : null,
      localCount: window.LGWishlistCore?.read()?.length ?? null,
      automaticSyncEnabled: false,
      themeOnly: true
    };
  }

  function disconnect() {
    sessionToken = null;
    accountItems = null;
    window.dispatchEvent(new CustomEvent('lg:wishlist:account-disconnected'));
    return status();
  }

  async function request(method, body) {
    checkStorefrontOrigin();
    if (!sessionToken) throw new Error('Connexion LG Wishlist requise.');

    let result;
    try {
      result = await fetch(WORKER_ORIGIN + '/api/account', {
        method,
        mode: 'cors',
        cache: 'no-store',
        headers: {
          'Authorization': 'Bearer ' + sessionToken,
          ...(body === undefined ? {} : {'Content-Type': 'application/json'})
        },
        ...(body === undefined ? {} : {body: JSON.stringify(body)}),
        signal: AbortSignal.timeout(12000)
      });
    } catch (error) {
      throw new Error('La connexion au serveur LG Wishlist a échoué.');
    }

    const payload = await result.json().catch(() => null);
    if (result.status === 401) disconnect();

    if (!result.ok) {
      const error = new Error(
        'LG Wishlist : ' + (payload?.error || 'erreur serveur')
      );
      error.status = result.status;
      throw error;
    }
    if (!Array.isArray(payload?.items)) {
      throw new Error('LG Wishlist : réponse de synchronisation invalide.');
    }
    accountItems = payload.items;
    window.dispatchEvent(new CustomEvent('lg:wishlist:account-ready', {
      detail: {count: accountItems.length}
    }));
    return {ok: true, items: accountItems, count: accountItems.length};
  }

  function connect() {
    try { checkStorefrontOrigin(); }
    catch (error) { return Promise.reject(error); }

    if (connecting) return connecting;
    if (sessionToken) return request('GET');

    connecting = new Promise((resolve, reject) => {
      let finished = false;
      let timeoutId;
      let popup;

      function done(error, token) {
        if (finished) return;
        finished = true;
        window.clearTimeout(timeoutId);
        window.removeEventListener('message', onMessage);

        if (error) {
          reject(error);
          return;
        }
        sessionToken = token;
        request('GET').then(resolve).catch(err => {
          disconnect();
          reject(err);
        });
      }

      function onMessage(event) {
        if (
          event.origin !== WORKER_ORIGIN ||
          event.source !== popup ||
          event.data?.source !== 'lg-wishlist'
        ) return;

        if (
          event.data.type !== 'auth-success' ||
          typeof event.data.token !== 'string' ||
          !event.data.token
        ) {
          done(new Error('Authentification Shopify non validée.'));
          return;
        }
        done(null, event.data.token);
      }

      window.addEventListener('message', onMessage);

      popup = window.open(
        WORKER_ORIGIN + '/auth/start',
        'lg-wishlist-auth',
        'width=520,height=720,scrollbars=yes,resizable=yes'
      );
      if (!popup) {
        done(new Error('Le navigateur a bloqué la fenêtre de connexion.'));
        return;
      }
      timeoutId = window.setTimeout(
        () => done(new Error('Connexion Shopify : délai dépassé.')),
        120000
      );
    }).finally(() => { connecting = null; });

    return connecting;
  }

  function readRemote() {
    return request('GET');
  }

  async function hydrateProductIds(items) {
    const result = items.map(item => ({...item}));
    const root = window.Shopify?.routes?.root || '/';
    let cursor = 0;
    let enriched = false;
    const failures = [];

    async function resolveOne() {
      while (cursor < result.length) {
        const index = cursor++;
        const item = result[index];
        if (item.product_id && /^\d+$/.test(String(item.product_id))) continue;
        try {
          const response = await fetch(
            root + 'products/' + encodeURIComponent(item.handle) + '.js',
            {credentials: 'same-origin', signal: AbortSignal.timeout(8000)}
          );
          if (!response.ok) throw new Error('product_unavailable');
          const product = await response.json();
          if (!/^\d+$/.test(String(product.id || ''))) throw new Error('id_unavailable');
          if (!Array.isArray(product.variants) ||
              !product.variants.some(v => String(v.id) === String(item.variant_id))) {
            throw new Error('variant_unavailable');
          }
          item.product_id = String(product.id);
          enriched = true;
        } catch {
          failures.push(item.handle);
        }
      }
    }

    await Promise.all(Array.from({length: Math.min(result.length, 4)}, resolveOne));
    if (enriched && window.LGWishlistCore?.write) {
      window.LGWishlistCore.write(result);
    }
    return {items: result, failures};
  }

  async function mergeLocal() {
    const core = window.LGWishlistCore;
    if (!core || typeof core.read !== 'function') {
      throw new Error('LG Wishlist : stockage local indisponible.');
    }
    if (!sessionToken) throw new Error('Connecte ton compte Shopify avant la fusion.');
    const local = core.read();
    if (!local.length) return readRemote();

    // On enrichit les anciens favoris sans changer leur variante.
    const {items, failures} = await hydrateProductIds(local);
    if (failures.length) {
      throw new Error(
        'Synchronisation suspendue : identifiant produit introuvable pour ' +
        failures.length + ' favori(s) (' + failures.slice(0, 3).join(', ') + '). ' +
        'Aucun favori local n’a été supprimé.'
      );
    }
    // Fusion demandée par la cliente, pas de transfert automatique en arrière-plan.
    return request('POST', {action: 'merge', items});
  }

  // Test uniquement : ajouter ?lg_sync_test=1 à l'URL de la page wishlist
  // dans la prévisualisation du thème brouillon.
  function showTestPanel() {
    const params = new URLSearchParams(location.search);
    if (params.get('lg_sync_test') !== '1' ||
        !document.querySelector('[data-lg-wishlist-lab]')) return;
    if (document.getElementById('lg-wishlist-sync-test')) return;

    const panel = document.createElement('aside');
    panel.id = 'lg-wishlist-sync-test';
    panel.setAttribute('aria-label', 'Test privé de synchronisation LG Wishlist');
    panel.style.cssText = [
      'position:fixed','bottom:12px','left:12px','right:12px',
      'z-index:99999','max-width:500px','margin:auto',
      'max-height:48vh','overflow:auto','padding:14px',
      'border:1px solid #555','border-radius:10px',
      'background:#fff','color:#111','box-shadow:0 5px 35px #0003',
      'font:14px/1.4 sans-serif'
    ].join(';');
    const title = document.createElement('strong');
    title.textContent = 'LG Wishlist : test de synchronisation (brouillon)';
    title.style.display = 'block';
    title.style.marginBottom = '10px';
    const output = document.createElement('p');
    output.setAttribute('role', 'status');
    output.setAttribute('aria-live', 'polite');
    output.textContent = 'Aucune synchronisation lancée. Tes favoris locaux sont conservés.';
    output.style.margin = '10px 0 0';
    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;';
    const close = document.createElement('button');
    close.type = 'button';
    close.textContent = 'Fermer';
    close.style.cssText = 'position:absolute;top:6px;right:10px;';
    close.addEventListener('click', () => panel.remove());

    function makeButton(label, action) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = label;
      button.style.cssText = 'padding:9px;border:1px solid #666;border-radius:5px;background:#f6f6f6;color:#111;';
      button.addEventListener('click', async () => {
        button.disabled = true;
        output.textContent = 'Opération en cours…';
        try {
          const res = await action();
          output.textContent = 'Succès. Favoris du compte Shopify : ' +
            res.count + '. Favoris sur cet appareil : ' +
            (window.LGWishlistCore?.read()?.length ?? 0) + '.';
        } catch (error) {
          output.textContent = 'Échec : ' + (error?.message || 'erreur inconnue');
        } finally {
          button.disabled = false;
        }
      });
      actions.appendChild(button);
    }
    makeButton('1. Connecter mon compte', connect);
    makeButton('2. Transférer mes favoris', mergeLocal);
    makeButton('3. Vérifier le compte', readRemote);
    panel.append(title, close, actions, output);
    document.body.appendChild(panel);
  }
  window.LGWishlistSync = Object.freeze({
    connect,
    disconnect,
    status,
    readRemote,
    mergeLocal
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', showTestPanel, {once:true});
  } else {
    showTestPanel();
  }
})();
